import { Alert, AlertItem } from '@vmware-react/clarity';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AssetsApi, AuthApi, SettingsApi } from 'vrlcm-gui-api';
import {
  AllowedAccessMap,
  AppSpec,
  AsyncOpState,
  FeatureToggle,
  User
} from 'vrlcm-gui-types';
import {
  accessConfigToAllowedAccessMap,
  AllowedAccessMapContext,
  appAuthorizationCheck,
  initTelemetry,
  isVcfAdmin,
  recentVisitedPageByUserUtil,
  redirectToLoginPage,
  UserContext
} from 'vrlcm-gui-utils';
import { AppLevelAlerts } from '../app-level-alerts';
import { AppLoadingSpinner } from '../app-loading-spinner';
import { espIdentifyUser } from '../esp/esp';
import { FirstBootPasswordUpdate } from '../first-boot-password-update';
import { l10n } from '../i18n/i18nUtil';
import { FeatureToggleContext } from '../use-feature-toggle/useFeatureToggle';
import { UserNotAllowedToAccessApp } from '../user-not-allowed-to-access-app';

interface Props {
  component: React.ComponentType;
  app: AppSpec;
}

const AppHost: React.FC<Props> = props => {
  const { app, component } = props;
  const [firstBootState, setFirstBootState] = useState<{
    fetchState: AsyncOpState;
    errorMessage?: string;
    data?: {
      loggedIn: boolean;
      user?: User;
      firstbootAdminPasswordChanged?: boolean;
      firstbootVcfAdminPasswordChanged?: boolean;
      allowedAccessMap?: AllowedAccessMap;
      featureToggle: FeatureToggle;
    };
  }>({
    fetchState: AsyncOpState.NotStarted
  });

  useEffect(() => {
    const newLogin = sessionStorage.getItem('newLogin');
    if (newLogin === 'true') {
      espIdentifyUser();
    }
    sessionStorage.removeItem('newLogin');
  }, []);

  const history = useHistory();
  useEffect(() => {
    SettingsApi.getTelemetryInformation().then(telemetryInfo => {
      const {
        collectorId,
        collectorInstanceId,
        environmentType,
        telemetryEnabled
      } = telemetryInfo;
      const isProduction = environmentType === 'production';
      initTelemetry(
        history,
        collectorId,
        collectorInstanceId,
        isProduction,
        telemetryEnabled
      );
    });
  }, [history]);

  useEffect(() => {
    history.listen(() => {
      if (firstBootState.data && firstBootState.data.user) {
        recentVisitedPageByUserUtil.addCurrentPage(
          firstBootState.data.user.name
        );
      }
    });
  }, [firstBootState.data, history]);

  useEffect(() => {
    /* On first time load - check if logged in*/
    setFirstBootState({
      fetchState: AsyncOpState.Inprogress
    });
    AuthApi.getUserData().then(
      ({ loggedIn, user }) => {
        if (loggedIn) {
          Promise.all([
            AuthApi.getUiLoginConfig(),
            AssetsApi.getAccessConfig(),
            AssetsApi.getFeatureToggle()
          ]).then(
            ([
              {
                firstbootAdminPasswordChanged,
                firstbootVcfAdminPasswordChanged
              },
              accessConfig,
              featureToggle
            ]) => {
              setFirstBootState({
                fetchState: AsyncOpState.Completed,
                data: {
                  loggedIn,
                  user,
                  firstbootAdminPasswordChanged,
                  firstbootVcfAdminPasswordChanged,
                  allowedAccessMap: user
                    ? accessConfigToAllowedAccessMap(accessConfig, user)
                    : undefined,
                  featureToggle
                }
              });
            },
            () =>
              setFirstBootState({
                fetchState: AsyncOpState.Failed
              })
          );
        } else {
          // no action needed because a forbidden redirect hook redirects the user to login page.
        }
      },
      () =>
        setFirstBootState({
          fetchState: AsyncOpState.Failed
        })
    );
  }, []);

  return (
    <div className="app-root">
      {firstBootState.fetchState === AsyncOpState.Inprogress && (
        <AppLoadingSpinner
          loadingText={l10n('components.withAppRoot.loadingApp', [app.name])}
        />
      )}
      {firstBootState.fetchState === AsyncOpState.Failed && (
        <div style={{ padding: '24px' }}>
          <Alert
            type="danger"
            ariaLiveMsg={l10n('components.withAppRoot.loadingFailed')}
            items={[
              <AlertItem>
                {l10n('components.withAppRoot.loadingFailed')}
              </AlertItem>
            ]}
          />
        </div>
      )}
      {firstBootState.fetchState === AsyncOpState.Completed && (
        <>
          {firstBootState.data &&
          firstBootState.data.loggedIn &&
          firstBootState.data.user &&
          firstBootState.data.allowedAccessMap ? (
            <>
              {(firstBootState.data.user.name === 'admin@local' &&
                !firstBootState.data.firstbootAdminPasswordChanged) ||
              (isVcfAdmin(firstBootState.data.user) &&
                !firstBootState.data.firstbootVcfAdminPasswordChanged) ? (
                <FirstBootPasswordUpdate user={firstBootState.data.user} />
              ) : (
                <FeatureToggleContext.Provider
                  value={firstBootState.data.featureToggle}
                >
                  <UserContext.Provider value={firstBootState.data.user}>
                    <AllowedAccessMapContext.Provider
                      value={firstBootState.data.allowedAccessMap}
                    >
                      {!appAuthorizationCheck(
                        firstBootState.data.user,
                        app.appId,
                        firstBootState.data.allowedAccessMap
                      ).allowed ? (
                        <UserNotAllowedToAccessApp
                          errorMessage={
                            appAuthorizationCheck(
                              firstBootState.data.user,
                              app.appId,
                              firstBootState.data.allowedAccessMap
                            ).errorMessage
                          }
                        />
                      ) : (
                        <div className="app-root-content-wrapper">
                          <AppLevelAlerts />
                          {React.createElement(component)}
                        </div>
                      )}
                    </AllowedAccessMapContext.Provider>
                  </UserContext.Provider>
                </FeatureToggleContext.Provider>
              )}
            </>
          ) : (
            redirectToLoginPage(true)
          )}
        </>
      )}
    </div>
  );
};

export default AppHost;
